<template>
  <div>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-2"
    />
    <p
      v-if="!loading && mainList.length === 0"
      class="text-center grey--text my-3"
    >
      Chưa có phiếu nhập xuất kho nào liên quan.
    </p>
    <table
      v-if="!loading && mainList.length > 0"
      class="table table-bordered table-hover"
    >
      <thead>
        <tr>
          <th class="text-center">Ngày</th>
          <th class="text-center">ID</th>
          <th class="text-center">Loại phiếu</th>
          <th class="text-left">Sản phẩm</th>
          <th class="text-right">Giá</th>
          <th class="text-right">SL</th>
          <th class="text-right">Tổng tiền</th>
          <th class="text-left">Ghi chú</th>
        </tr>
      </thead>
      <tbody v-for="slip in mainList" :key="slip.id">
        <tr v-for="(item, idx) in slip.listDetail" :key="item.id">
          <td
            v-if="idx === 0"
            :rowspan="slip.listDetail.length"
            class="text-center align-middle"
          >
            {{ formatDate(slip.createdAt, 'DD/MM/YYYY') }}
          </td>
          <td
            v-if="idx === 0"
            :rowspan="slip.listDetail.length"
            class="text-left align-middle"
          >
            <div>{{ slip.storeName }}</div>
            <a
              :href="getStockSlipLink(slip)"
              target="_self"
              class="font-weight-bold"
            >
              {{ slip.code }}
            </a>
          </td>
          <td
            v-if="idx === 0"
            :rowspan="slip.listDetail.length"
            class="text-center align-middle"
          >
            {{ getTypeName(slip.type) }}
          </td>
          <td class="text-left">
            <div>{{ item.productName }} ({{ item.productCode }})</div>
            <div v-if="item.chassisNumber" class="text-small text-primary">
              SK: {{ item.chassisNumber }}
            </div>
            <div v-if="item.engineNumber" class="text-small text-primary">
              SM: {{ item.engineNumber }}
            </div>
            <div v-if="item.serialNumber" class="text-small text-primary">
              Serial: {{ item.serialNumber }}
            </div>
          </td>
          <td class="text-right">{{ convertPrice(item.unitPrice) }}</td>
          <td class="text-right">{{ item.quantity }}</td>
          <td
            v-if="idx === 0"
            :rowspan="slip.listDetail.length"
            class="text-right align-middle"
          >
            {{ convertPrice(slip.totalAmount) }}
          </td>
          <td
            v-if="idx === 0"
            :rowspan="slip.listDetail.length"
            class="text-left align-middle"
          >
            {{ slip.description }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { formatDate, convertPrice } from '@/utils/common';
import PurchaseOrderService from '@/core/api/purchase-order/purchase-order.service';
import PurchaseOrderShareService from '@/core/api/purchase-order/purchase-order-share.service';

const STOCK_SLIP_TYPE_NAME = {
  REQUIRE_IMPORT_STOCK: 'Yêu cầu nhập kho',
  IMPORT_STOCK: 'Nhập kho NCC',
  IMPORT_STOCK_FROM_BILL: 'Nhập trả hàng',
  IMPORT_CHANGE_PRODUCT: 'Nhập đổi sản phẩm',
  EXPORT_STOCK: 'Xuất kho',
  EXPORT_STOCK_TO_SALE: 'Xuất bán lẻ',
  EXPORT_STOCK_TO_SALE_WHOLSE: 'Xuất bán buôn',
  EXPORT_WARRANTY_TO_PROVIDER: 'Xuất trả NCC',
  EXPORT_CHANGE_PRODUCT: 'Xuất đổi sản phẩm',
  TRANSFER_STOCK: 'Chuyển kho',
  REQUIRE_EXPORT_STOCK: 'Yêu cầu xuất kho',
};

export default {
  name: 'POStockSlipTransaction',
  props: {
    purchaseOrderId: { type: [Number, String], required: true },
    mode: { type: String, default: 'single' }, // 'single' | 'aggregate'
  },
  data() {
    return {
      mainList: [],
      loading: false,
    };
  },
  watch: {
    purchaseOrderId: {
      immediate: true,
      handler(val) {
        if (val) this.fetchData();
      },
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const fetcher =
          this.mode === 'aggregate'
            ? PurchaseOrderShareService.listStockSlipsAggregate(
                this.purchaseOrderId,
              )
            : PurchaseOrderService.listStockSlips(this.purchaseOrderId);
        const res = await fetcher;
        // ResponseInterceptor wrap: res.data = { status, statusCode, message, data }
        this.mainList = (res?.data?.data || []).map((slip) => ({
          ...slip,
          listDetail: slip.listDetail || [],
        }));
      } catch (e) {
        this.mainList = [];
      } finally {
        this.loading = false;
      }
    },
    formatDate,
    convertPrice,
    getTypeName(type) {
      return STOCK_SLIP_TYPE_NAME[type] || 'Không rõ';
    },
    getStockSlipLink(slip) {
      // Routing theo type — tham chiếu router.js paths đã verify
      const importTypes = [
        'IMPORT_STOCK',
        'IMPORT_STOCK_FROM_BILL',
        'IMPORT_CHANGE_PRODUCT',
        'REQUIRE_IMPORT_STOCK',
      ];
      const exportTypes = [
        'EXPORT_STOCK',
        'EXPORT_STOCK_TO_SALE',
        'EXPORT_STOCK_TO_SALE_WHOLSE',
        'EXPORT_WARRANTY_TO_PROVIDER',
        'EXPORT_CHANGE_PRODUCT',
        'REQUIRE_EXPORT_STOCK',
      ];
      if (importTypes.includes(slip.type)) {
        return `#/stocks/update-stock?id=${slip.id}`;
      }
      if (exportTypes.includes(slip.type)) {
        return `#/export-stocks/update-export-stock?id=${slip.id}`;
      }
      if (slip.type === 'TRANSFER_STOCK') {
        return `#/transfer-stocks`;
      }
      return `#/stocks`;
    },
  },
};
</script>

<style scoped>
.text-small {
  font-size: 12px;
  font-style: italic;
}
.align-middle {
  vertical-align: middle !important;
}
/* Group hover: hover bất kỳ row con nào → highlight toàn bộ tbody (slip).
   Override table-hover của Bootstrap (chỉ highlight tr đang hover). */
.table-hover > tbody:hover > tr {
  background-color: rgba(0, 0, 0, 0.075);
}
</style>
